/*
 * The three toolbar icons.
 *
 * Inline SVG rather than unicode glyphs, unlike the formatting buttons beside
 * them. Those are letters and punctuation by convention - B, I, H2, "1." - and
 * a font is the right way to draw a letter. "An article", "an image" and "a
 * video" have no such convention, and the unicode symbols that come close
 * (🖼, ▶, 🗎) are either emoji that render in colour and at the wrong weight,
 * or rare enough to arrive as a tofu box on a machine without the font.
 *
 * Each is stroked in `currentColor`, so a button's colour, hover and disabled
 * states apply to the icon without a second set of rules, and each is
 * aria-hidden: the accessible name lives on the button, which is the thing
 * being pressed. Never use one without a label on that button - an icon-only
 * control with no accessible name is unusable by anything but a mouse.
 */

const SHARED = {
  width: 16,
  height: 16,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
};

/**
 * A page with text on it: "another article".
 *
 * Deliberately NOT a chain link - the toolbar already has one of those for an
 * ordinary markdown link, and two chains would be a coin toss.
 */
export function ArticleLinkIcon(): JSX.Element {
  return (
    <svg {...SHARED}>
      <path d="M3.5 2.25h5.25l3.75 3.75v7.75a.5.5 0 0 1-.5.5H3.5a.5.5 0 0 1-.5-.5V2.75a.5.5 0 0 1 .5-.5Z" />
      <path d="M8.75 2.25V6h3.75" />
      <path d="M5.75 9h4.5M5.75 11.25h3" />
    </svg>
  );
}

/** A framed picture: sun and horizon, the long-standing shape for an image. */
export function ImageIcon(): JSX.Element {
  return (
    <svg {...SHARED}>
      <rect x="2.25" y="3" width="11.5" height="10" rx="1" />
      <circle cx="5.75" cy="6.25" r="1.1" />
      <path d="M2.5 11.25 6 8.25l2.25 2 2-1.75 3.25 2.75" />
    </svg>
  );
}

/** A screen with a play triangle: "a video plays here". */
export function VideoIcon(): JSX.Element {
  return (
    <svg {...SHARED}>
      <rect x="2.25" y="3" width="11.5" height="10" rx="1" />
      <path d="M6.75 6.25l3.5 1.75-3.5 1.75V6.25Z" fill="currentColor" stroke="none" />
    </svg>
  );
}
